import { VStack } from '@chakra-ui/react';
import { useEditable } from '~/pages/projects/hooks/useEditable';
import { FinanceOverview } from './FinanceOverview';
import { useProjectParams } from '../../hooks/useProjectParams';
import { FinanceOfferForm } from './FinanceOfferForm';
import { FinanceInvoiceForm } from './FinanceInvoiceForm';
import { MultiModal } from '~/components/MultiModal';
import { SelectGeneralAggrementsModalContent } from './SelectGeneralAggrementsModalContent';
import { calculationReducer, combineReducers, modalReducer, rowReducer } from '~/reducers';
import { FreePositionModalContent } from './FinanceFreePositionModalContent';
import { GeneralAgreementModalContent } from './FinanceGeneralAgreementModalContent';
import { useModal } from '~/hooks';
import { useIsStandardGeneralAgreementId } from '~/pages/projects/hooks/useIsStandardGeneralAgreementId';
import { WorkdescriptionModal } from '../WorkdescriptionModal';
import { financeMachine } from './financeMachine';
import { useFinanceServices } from './hooks/useFinanceServices';
import { ProjectIds } from '../../types';
import { LOCAL_STORAGE_KEY } from './constants';
import { usePdfRefetchQueries } from '../../hooks/usePdf';
import { useViewer, usePersistedMachine } from '~/hooks';
import { join } from '~/utils';
import { isPxConnectSource, serviceRequestIdFromMetadata } from '~/meta/data/px-connect';
import { PxConnectModal } from '../OperationalReports/PxConnect';
import { PxConnectSchadenberichtBlock } from './PxConnectSchadenberichtBlock';
import log from '~/log';

export const Modals = {
    EditFreePosition: 'EditFreePosition',
    SelectGeneralAggrements: 'SelectGeneralAggrements',
    SelectStandardGeneralAggrements: 'SelectStandardGeneralAggrements',
    EditGeneralAggrementPosition: 'EditGeneralAggrementPosition',
    Workdescription: 'Workdescription',
    PxConnect: 'PxConnect',
};

export function Finance() {
    const viewer = useViewer();
    const canCreateInvoice = viewer?.hasPermission('additional.createInvoice', 'enabled');
    log.debug('Finance.permissions', { canCreateInvoice });
    const { projectId, subprojectId } = useProjectParams();
    const { refetchQueries } = usePdfRefetchQueries({ subprojectId });
    const { editable, editType } = useEditable();
    const isOffer = editable && editType === 'offer';
    const isInvoice = editable && editType === 'invoice';
    const services = useFinanceServices({ refetchQueries, subprojectId });
    const sessionStorageKey = join([LOCAL_STORAGE_KEY, projectId, subprojectId], '-');
    const isStandard: any = useIsStandardGeneralAgreementId();
    const [state, send] = usePersistedMachine(financeMachine, {
        sessionStorageKey,
        services,
        // Ohne `subproject` im Context ist der persistierte State eine Falle (UCPWO-398):
        // `init` ist `type: 'parallel'` mit `onDone: 'idle'`, ein gescheitertes
        // `getSubproject` erreicht also trotzdem `idle` — und dieser Zustand wurde
        // persistiert. Beim nächsten Vollreload nimmt die Machine ihn wieder auf,
        // überspringt `init` und lädt nie nach: kein Schadenbericht-Block (der hängt an
        // `subproject.metadata`), leere Angebots- und Kalkulationsliste, für die restliche
        // Tab-Sitzung. Nur ein client-seitiger Wechsel half, weil erst der Unmount den
        // Eintrag löscht. `subproject` wird ausschließlich in `getSubproject.onDone`
        // gesetzt und nie geleert — die Bedingung verwirft also nur kaputte Zustände.
        canResume: (persisted: any) => Boolean(persisted?.context?.subproject),
        context: {
            projectId,
            subprojectId,
            isOffer,
            isInvoice,
            // isStandard: 2543,
            isStandard,
        },
    });
    const { subproject } = state.context;
    const loading = !state.matches('idle');
    const projectIds: ProjectIds = { subprojectId, projectId };

    const props: any = {
        ...projectIds,
        send,
        context: state?.context,
    };

    // Der Schadenbericht-Block erscheint auf JEDER pxconnect-Kalkulationsseite (ADR 0006) —
    // nicht nur bei Leckortung, weil der Bericht auch aus der Trocknung gepflegt wird.
    const isPxConnect = isPxConnectSource(subproject?.metadata);
    const serviceRequestId = serviceRequestIdFromMetadata(subproject?.metadata);
    const schadensobjekt = [
        subproject?.projekt?.strasse,
        [subproject?.projekt?.plz, subproject?.projekt?.ort].filter(Boolean).join(' '),
    ]
        .filter(Boolean)
        .join(', ');
    const schadenNr = subproject?.schadenNr ?? undefined;
    // ADR 0005 / Berechtigungs-Spec: Ansehen ist frei ("ansehen darf jeder") — der
    // Block erscheint auf jeder pxconnect-Kalkulationsseite. Erstellen/Bearbeiten
    // hängt an `create`, Löschen an `delete` (beides an den Block durchgereicht).
    const canCreateSchadenbericht = viewer?.hasPermission('pxconnect.schadenbericht', 'create');
    const canDeleteSchadenbericht = viewer?.hasPermission('pxconnect.schadenbericht', 'delete');
    const showPxConnect = isPxConnect && Boolean(serviceRequestId);

    const { dispatch, onOpen } = useModal({
        defaultState: { modals: { activeModal: Modals.EditFreePosition } },
        dependencies: [state?.context, isStandard],
        component: (
            <MultiModal>
                <FreePositionModalContent id={Modals.EditFreePosition} {...props} />
                <SelectGeneralAggrementsModalContent
                    id={Modals.SelectGeneralAggrements}
                    generalAgreementId={state?.context?.generalAgreementId}
                    {...props}
                />
                <SelectGeneralAggrementsModalContent
                    id={Modals.SelectStandardGeneralAggrements}
                    generalAgreementId={isStandard}
                    {...props}
                />
                <GeneralAgreementModalContent id={Modals.EditGeneralAggrementPosition} {...props} />
                <WorkdescriptionModal id={Modals.Workdescription} />
                <PxConnectModal
                    id={Modals.PxConnect}
                    projectId={projectId}
                    subprojectId={subprojectId}
                    serviceRequestId={serviceRequestId}
                    schadensobjekt={schadensobjekt}
                    schadenNr={schadenNr}
                    subprojectMetadata={subproject?.metadata}
                />
            </MultiModal>
        ),
        reducer: combineReducers({
            modals: modalReducer(Modals.EditFreePosition),
            row: rowReducer,
            calculation: calculationReducer,
        }),
    });

    const onOpenPxConnect = () => {
        dispatch?.({
            type: 'setModal',
            data: { modal: Modals.PxConnect, props: { scrollBehavior: 'inside' } },
        });
        onOpen?.();
    };

    return (
        <VStack spacing={6} p={6}>
            {isOffer && (
                <FinanceOfferForm
                    viewer={viewer}
                    send={send}
                    context={state?.context}
                    loading={state.matches('updateOffer') || state.matches('createOffer')}
                    {...projectIds}
                />
            )}
            {isInvoice && (
                <FinanceInvoiceForm
                    viewer={viewer}
                    send={send}
                    context={state?.context}
                    loading={state.matches('updateInvoice') || state.matches('createInvoice')}
                    {...projectIds}
                />
            )}
            {!editable && (
                <FinanceOverview
                    send={send}
                    subproject={subproject}
                    context={state?.context}
                    loading={loading}
                    viewer={viewer}
                    {...projectIds}
                />
            )}
            {/* Schadenbericht-Block unterhalb von Angebote/Rechnungen + Kalkulation (ADR 0006). */}
            {!editable && showPxConnect && (
                <PxConnectSchadenberichtBlock
                    projectId={projectId}
                    serviceRequestId={serviceRequestId}
                    onOpenPxConnect={onOpenPxConnect}
                    canCreate={Boolean(canCreateSchadenbericht)}
                    canDelete={Boolean(canDeleteSchadenbericht)}
                />
            )}
        </VStack>
    );
}
